import { Ban, CheckCircle, Clock, Loader2, XCircle } from 'lucide-react'
import type { WorkTaskStatus } from '../../types/work'

interface WorkStatusBadgeProps {
  status: WorkTaskStatus
  size?: 'sm' | 'md'
}

const STATUS_CONFIG: Record<WorkTaskStatus, {
  label: string
  icon: React.ReactNode
  style: React.CSSProperties
}> = {
  pending: {
    label: 'Pending',
    icon: <Clock size={12} />,
    style: {
      background: 'rgba(234, 179, 8, 0.15)',
      color: 'var(--warning, #eab308)',
      border: '1px solid rgba(234, 179, 8, 0.3)',
    },
  },
  processing: {
    label: 'Processing',
    icon: <Loader2 size={12} className="work-badge-spin" />,
    style: {
      background: 'rgba(59, 130, 246, 0.15)',
      color: 'var(--info, #3b82f6)',
      border: '1px solid rgba(59, 130, 246, 0.3)',
    },
  },
  completed: {
    label: 'Completed',
    icon: <CheckCircle size={12} />,
    style: {
      background: 'rgba(16, 185, 129, 0.15)',
      color: 'var(--success, #10b981)',
      border: '1px solid rgba(16, 185, 129, 0.3)',
    },
  },
  failed: {
    label: 'Failed',
    icon: <XCircle size={12} />,
    style: {
      background: 'rgba(239, 68, 68, 0.15)',
      color: 'var(--danger, #ef4444)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
    },
  },
  cancelled: {
    label: 'Cancelled',
    icon: <Ban size={12} />,
    style: {
      background: 'rgba(107, 114, 128, 0.15)',
      color: 'var(--text-muted, #6b7280)',
      border: '1px solid rgba(107, 114, 128, 0.3)',
    },
  },
}

export default function WorkStatusBadge({ status, size = 'md' }: WorkStatusBadgeProps) {
  const config = STATUS_CONFIG[status]
  const fontSize = size === 'sm' ? '11px' : '12px'
  const padding = size === 'sm' ? '2px 6px' : '3px 8px'

  return (
    <>
      <style>{`
        @keyframes work-badge-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .work-badge-spin {
          animation: work-badge-spin 1s linear infinite;
        }
      `}</style>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding,
          borderRadius: '20px',
          fontSize,
          fontWeight: 500,
          whiteSpace: 'nowrap',
          ...config.style,
        }}
      >
        {config.icon}
        {config.label}
      </span>
    </>
  )
}
